/**
 * Admin Organization Detail Page
 *
 * Tabbed interface for managing a single organization.
 */

import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { logAdminAction, ACTION_DESCRIPTIONS, createDiff } from '@/lib/admin/auditLog';
import { AdminPageHeader, AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Users,
  CreditCard,
  Settings,
  Shield,
  ClipboardCheck,
  ScrollText,
  Loader2,
  Building2,
  AlertTriangle,
} from 'lucide-react';
import { Org, OrgEntitlements, OrgScopeConfig } from '@/integrations/firebase/types';

// Tab components (we'll create simplified versions inline)
import { OrgUsersTab } from '@/components/admin/tabs/OrgUsersTab';
import { OrgEntitlementsTab } from '@/components/admin/tabs/OrgEntitlementsTab';
import { OrgScopeTab } from '@/components/admin/tabs/OrgScopeTab';
import { OrgSupportTab } from '@/components/admin/tabs/OrgSupportTab';
import { OrgRunsTab } from '@/components/admin/tabs/OrgRunsTab';
import { OrgAuditTab } from '@/components/admin/tabs/OrgAuditTab';

export default function AdminOrgDetail() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { adminUser, isSuperadmin, canImpersonate } = useAdminAuth();
  const { enterSupportMode, isInSupportMode, actingOrgId } = useSupportMode();
  const { toast } = useToast();

  const [org, setOrg] = useState<Org | null>(null);
  const [entitlements, setEntitlements] = useState<OrgEntitlements | null>(null);
  const [scopeConfig, setScopeConfig] = useState<OrgScopeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'users');

  const isActingAsThisOrg = isInSupportMode && actingOrgId === orgId;

  // Fetch org data
  useEffect(() => {
    const fetchOrgData = async () => {
      if (!orgId) return;

      try {
        setLoading(true);

        // Fetch org
        const orgDoc = await getDoc(doc(db, 'orgs', orgId));
        if (!orgDoc.exists()) {
          toast({
            title: 'Not Found',
            description: 'Organization not found',
            variant: 'destructive',
          });
          navigate('/admin/orgs');
          return;
        }

        setOrg({ id: orgDoc.id, ...orgDoc.data() } as Org);

        // Fetch entitlements
        const entitlementsDoc = await getDoc(doc(db, 'org_entitlements', orgId));
        if (entitlementsDoc.exists()) {
          setEntitlements(entitlementsDoc.data() as OrgEntitlements);
        }

        // Fetch scope config
        const scopeDoc = await getDoc(doc(db, 'org_scope_configs', orgId));
        if (scopeDoc.exists()) {
          setScopeConfig(scopeDoc.data() as OrgScopeConfig);
        }
      } catch (error) {
        console.error('Error fetching org:', error);
        toast({
          title: 'Error',
          description: 'Failed to load organization',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrgData();
  }, [orgId, navigate, toast]);

  const handleSuspendOrg = async () => {
    if (!org || !user?.uid || !adminUser) return;

    try {
      const newStatus = org.status === 'active' ? 'suspended' : 'active';

      await updateDoc(doc(db, 'orgs', org.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });

      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: newStatus === 'suspended' ? 'SUSPEND_ORG' : 'ACTIVATE_ORG',
        targetOrgId: org.id,
        description:
          newStatus === 'suspended'
            ? ACTION_DESCRIPTIONS.SUSPEND_ORG(org.name || org.id)
            : ACTION_DESCRIPTIONS.ACTIVATE_ORG(org.name || org.id),
        diff: createDiff({ status: org.status }, { status: newStatus }),
      });

      setOrg((prev) => (prev ? { ...prev, status: newStatus } : null));

      toast({
        title: newStatus === 'suspended' ? 'Organization Suspended' : 'Organization Activated',
        description: `${org.name || 'Organization'} is now ${newStatus}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleEnterSupportMode = async () => {
    if (!org) return;

    const result = await enterSupportMode(org.id);
    if (result.success) {
      toast({
        title: 'Support Mode Activated',
        description: `Now acting as ${org.name || org.id}`,
      });
    } else {
      toast({
        title: 'Failed',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!org) {
    return null;
  }

  return (
    <div>
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/orgs')}
        className="mb-4 text-gray-400 hover:text-white -ml-2"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to Organizations
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-lg bg-[hsl(220,15%,15%)] flex items-center justify-center">
            <Building2 className="h-7 w-7 text-gray-400" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-white">{org.name || 'Unnamed Organization'}</h1>
              <Badge
                variant="outline"
                className={
                  org.status === 'active'
                    ? 'border-green-500/50 text-green-400'
                    : 'border-red-500/50 text-red-400'
                }
              >
                {org.status}
              </Badge>
              {entitlements && (
                <Badge
                  variant="outline"
                  className="border-[hsl(220,15%,30%)] text-gray-300 capitalize"
                >
                  {entitlements.planTier}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1">{org.primaryContactEmail}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canImpersonate && !isActingAsThisOrg && (
            <Button
              variant="outline"
              onClick={handleEnterSupportMode}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
            >
              <Shield className="h-4 w-4 mr-2" />
              Enter Support Mode
            </Button>
          )}
          {isActingAsThisOrg && (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40">
              <Shield className="h-3 w-3 mr-1" />
              Currently Acting As
            </Badge>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,20%)] mb-6">
          <TabsTrigger
            value="users"
            className="data-[state=active]:bg-[hsl(220,70%,50%)] data-[state=active]:text-white"
          >
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger
            value="plan"
            className="data-[state=active]:bg-[hsl(220,70%,50%)] data-[state=active]:text-white"
            disabled={!isSuperadmin}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Plan
            {!isSuperadmin && <span className="text-xs ml-1">(Locked)</span>}
          </TabsTrigger>
          <TabsTrigger
            value="scope"
            className="data-[state=active]:bg-[hsl(220,70%,50%)] data-[state=active]:text-white"
          >
            <Settings className="h-4 w-4 mr-2" />
            Scope
          </TabsTrigger>
          <TabsTrigger
            value="support"
            className="data-[state=active]:bg-[hsl(220,70%,50%)] data-[state=active]:text-white"
          >
            <Shield className="h-4 w-4 mr-2" />
            Support Tools
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            className="data-[state=active]:bg-[hsl(220,70%,50%)] data-[state=active]:text-white"
          >
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Runs
          </TabsTrigger>
          <TabsTrigger
            value="audit"
            className="data-[state=active]:bg-[hsl(220,70%,50%)] data-[state=active]:text-white"
          >
            <ScrollText className="h-4 w-4 mr-2" />
            Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <OrgUsersTab orgId={org.id} orgName={org.name || org.id} />
        </TabsContent>

        <TabsContent value="plan">
          {isSuperadmin ? (
            <OrgEntitlementsTab
              orgId={org.id}
              entitlements={entitlements}
              onUpdate={setEntitlements}
            />
          ) : (
            <AdminCard>
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                <p>Superadmin access required to modify plan settings</p>
              </div>
            </AdminCard>
          )}
        </TabsContent>

        <TabsContent value="scope">
          <OrgScopeTab
            orgId={org.id}
            scopeConfig={scopeConfig}
            onUpdate={setScopeConfig}
          />
        </TabsContent>

        <TabsContent value="support">
          <OrgSupportTab org={org} />
        </TabsContent>

        <TabsContent value="runs">
          <OrgRunsTab orgId={org.id} />
        </TabsContent>

        <TabsContent value="audit">
          <OrgAuditTab orgId={org.id} />
        </TabsContent>
      </Tabs>

      {/* Danger Zone */}
      <AdminCard className="mt-8 border-red-500/30">
        <h3 className="text-lg font-medium text-white mb-4">Danger Zone</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-300">
              {org.status === 'active' ? 'Suspend Organization' : 'Activate Organization'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {org.status === 'active'
                ? 'Suspend access for all users in this organization'
                : 'Restore access for all users in this organization'}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleSuspendOrg}
            className={
              org.status === 'active'
                ? 'border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300'
                : 'border-green-500/50 text-green-400 hover:bg-green-500/20 hover:text-green-300'
            }
          >
            {org.status === 'active' ? 'Suspend' : 'Activate'}
          </Button>
        </div>
      </AdminCard>
    </div>
  );
}
